import {getDecimalSeparator, getThousandsSeparator} from '../localization';

const MIN_INTEGER = -2_147_483_648;
const MAX_INTEGER = 2_147_483_647;

const MIN_LONG = -999_999_999_999_999;
const MAX_LONG = 999_999_999_999_999;

/**
 * Determines if a string represents a valid integer value.
 *
 * @param value The string to test.
 * @return true if the string is a valid integer; false if not
 */
export const isValidInteger = (value: string) => {
   return !isNaN(parseInteger(value));
};

/**
 * Determines if a string represents a valid long value.
 *
 * @param value The string to test.
 * @return true if the string is a valid long; false if not
 */
export const isValidLong = (value: string) => {
   return !isNaN(parseLong(value));
};

/**
 * Determines if a string represents a valid decimal value.
 *
 * @param value The string to test.
 * @return true if the string is a valid decimal; false if not
 */
export const isValidDecimal = (value: string) => {
   return !isNaN(parseDecimal(value));
};

/**
 * Parse a string to a number, stripping off any numbers have the decimal point.
 *
 * @param value The string to parse.
 * @return The parsed number, NaN if the string is invalid.
 */
export const parseInteger = (value: string): number => {
   const long = parseLong(value);
   if (!isNaN(long)) {
      if (long < MIN_INTEGER || long > MAX_INTEGER) {
         return NaN;
      }
   }
   return long;
};

/**
 * Parse a string to a number, stripping off any numbers have the decimal point.
 *
 * @param value The string to parse.
 * @return The parsed number, NaN if the string is invalid.
 */
export const parseLong = (value: string): number => {
   const thousandsSeparator = getThousandsSeparator();
   const decimalSeparator = getDecimalSeparator();
   const decimalSeparatorLocation = value.indexOf(decimalSeparator);

   if (decimalSeparatorLocation >= 0) {
      const intValue = parseInt(value.substring(0, decimalSeparatorLocation)
                                   .replace(new RegExp(`[\\${thousandsSeparator}]`, 'g'), ''), 10);

      if (intValue < MIN_LONG || intValue > MAX_LONG) {
         return NaN;
      }

      return intValue;
   }

   const intValue = parseInt(value.replace(new RegExp(`[\\${thousandsSeparator}]`, 'g'), ''), 10);

   if (intValue < MIN_LONG || intValue > MAX_LONG) {
      return NaN;
   }

   return intValue;
};

/**
 * Parse a string to a number, keeping numbers after the decimal point.
 *
 * @param value The string to parse.
 * @return The parsed number, NaN if the string is invalid.
 */
export const parseDecimal = (value: string): number => {
   const thousandsSeparator = getThousandsSeparator();
   const decimalSeparator = getDecimalSeparator();
   const text = value.replace(new RegExp(`[${decimalSeparator}]`, 'g'), '***zzz***')
                     .replace(new RegExp(`[\\${thousandsSeparator}]`, 'g'), '')
                     .replace('***zzz***', '.');

   return parseFloat(text);
};
